import * as React from "react";
import { format, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface DatePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** Inclusive upper bound. Days strictly after this become unselectable. */
  maxDate?: string;
  /** Inclusive lower bound. Days strictly before this become unselectable. */
  minDate?: string;
}

function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  className,
  maxDate,
  minDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);

  const date = value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  const max = maxDate ? parse(maxDate, "yyyy-MM-dd", new Date()) : undefined;
  const min = minDate ? parse(minDate, "yyyy-MM-dd", new Date()) : undefined;

  const isValidDate = date && !isNaN(date.getTime());
  const disabled = React.useMemo(() => {
    const validMax = max && !isNaN(max.getTime()) ? max : undefined;
    const validMin = min && !isNaN(min.getTime()) ? min : undefined;
    if (validMax && validMin) return { before: validMin, after: validMax };
    if (validMax) return { after: validMax };
    if (validMin) return { before: validMin };
    return undefined;
  }, [max, min]);

  function handleSelect(selected: Date | undefined) {
    if (selected) {
      onChange?.(format(selected, "yyyy-MM-dd"));
    }
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className={cn(
              "h-8 w-full justify-start px-2.5 text-left text-sm font-normal",
              !isValidDate && "text-muted-foreground",
              className,
            )}
          />
        }
      >
        <CalendarIcon className="mr-1.5 size-3.5 text-muted-foreground" />
        {isValidDate ? format(date, "dd MMM yyyy") : placeholder}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={isValidDate ? date : undefined}
          onSelect={handleSelect}
          defaultMonth={isValidDate ? date : undefined}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
